
import { Stack } from 'ui/stack';

import { Select } from 'components/partials/select/select';

export interface RegimeOption {
  value: string | number;
  label: string;
}

export interface RangeOption {
  value: string;
  label: string;
}

export interface RankOption {
  value: number;
  label: string;
}

export interface SpecialAwardRegimesSourceProps {
  regimeOptions: RegimeOption[];
  selectedRegimeIds: Array<string | number>;
  onRegimesChange: (ids: Array<string | number>) => void;
  rangeOptions: RangeOption[];
  selectedRangeKeys: string[];
  onRangesChange: (keys: string[]) => void;
  rangeLabel: string;
  rankOptions: RankOption[];
  selectedRankIds: number[];
  onRanksChange: (ids: number[]) => void;
  isRanksLoading?: boolean;
}

const PORTAL_STYLES = (): Record<string, unknown> => ({
  menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 9999 }),
});

const toValues = <TOption extends { value: string | number }>(
  value: TOption | TOption[] | null
): TOption['value'][] => {
  if (!value) {
    return [];
  }

  return (Array.isArray(value) ? value : [value]).map((option) => option.value);
};

const SpecialAwardRegimesSource = ({
  regimeOptions,
  selectedRegimeIds,
  onRegimesChange,
  rangeOptions,
  selectedRangeKeys,
  onRangesChange,
  rangeLabel,
  rankOptions,
  selectedRankIds,
  onRanksChange,
  isRanksLoading = false,
}: SpecialAwardRegimesSourceProps) => {
  const selectedRegimeOptions = regimeOptions.filter((option) =>
    selectedRegimeIds.some((id) => String(id) === String(option.value))
  );
  const selectedRangeOptions = rangeOptions.filter((option) =>
    selectedRangeKeys.includes(option.value)
  );
  const selectedRankOptions = rankOptions.filter((option) =>
    selectedRankIds.includes(option.value)
  );

  const hasRegimes = selectedRegimeIds.length > 0;

  return (
    <Stack
      sx={{ gap: 1.5, pr: '54px' }}
      data-testid="special-award-regimes-source"
      data-modal-keyboard-ignore
    >
      <Select<RegimeOption>
        label="Regime"
        labelInTop
        required
        isMulti
        isClearable
        options={regimeOptions}
        value={selectedRegimeOptions}
        onChange={(value) => onRegimesChange(toValues(value))}
        placeholder=""
        menuPortalTarget={document.body}
        extraStyles={PORTAL_STYLES}
        customClass="mt-0 text-md"
        extraBasicStyles={{ minHeight: 40, padding: 0 }}
        data-testid="special-award-regimes-select"
      />

      <Select<RangeOption>
        label={rangeLabel}
        labelInTop
        isMulti
        isClearable
        options={rangeOptions}
        value={selectedRangeOptions}
        onChange={(value) => onRangesChange(toValues(value))}
        disabled={!hasRegimes}
        placeholder=""
        menuPortalTarget={document.body}
        extraStyles={PORTAL_STYLES}
        customClass="mt-0 text-md"
        extraBasicStyles={{ minHeight: 40, padding: 0 }}
        data-testid="special-award-ranges-select"
      />

      <Select<RankOption>
        label="Academic / Scholarship"
        labelInTop
        isMulti
        isClearable
        options={rankOptions}
        value={selectedRankOptions}
        onChange={(value) => onRanksChange(toValues(value))}
        disabled={!hasRegimes}
        isLoading={isRanksLoading}
        placeholder=""
        menuPortalTarget={document.body}
        extraStyles={PORTAL_STYLES}
        customClass="mt-0 text-md"
        extraBasicStyles={{ minHeight: 40, padding: 0 }}
        data-testid="special-award-ranks-select"
      />
    </Stack>
  );
};

export default SpecialAwardRegimesSource;
